/**
 * HarveyBall — SVG circle visualization for bit classification values
 *
 * Visual representation:
 *   value 1 = 25% filled (quarter)
 *   value 2 = 50% filled (half)
 *   value 3 = 75% filled (three-quarter)
 *   value 4 = 100% filled (full)
 *
 * @param {number} value  — 1-4
 * @param {number} size   — pixel width/height (default 20)
 */
export default function HarveyBall({ value, size = 20 }) {
  const numValue = Number(value);
  if (!Number.isFinite(numValue) || numValue < 1 || numValue > 4) {
    return null;
  }

  const center = size / 2;
  const radius = (size / 2) - 2; // leave room for stroke

  // Value 4 → fully filled circle
  if (numValue === 4) {
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="flex-shrink-0"
        aria-label={`${numValue} of 4`}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="#101010"
          stroke="#101010"
          strokeWidth={1.5}
        />
      </svg>
    );
  }

  // Values 1-3 → arc wedge
  const sweepAngle = (numValue / 4) * 360;
  const toRad = (deg) => (deg * Math.PI) / 180;

  // Start at 12 o'clock (angle offset -90°)
  const startAngle = -90;
  const endAngle = startAngle + sweepAngle;

  const startX = center + radius * Math.cos(toRad(startAngle));
  const startY = center + radius * Math.sin(toRad(startAngle));
  const endX = center + radius * Math.cos(toRad(endAngle));
  const endY = center + radius * Math.sin(toRad(endAngle));

  const largeArc = sweepAngle > 180 ? 1 : 0;

  const arcPath = `M ${center} ${center} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="flex-shrink-0"
      aria-label={`${numValue} of 4`}
    >
      {/* Background circle */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#D6D6DA"
        strokeWidth={1.5}
      />
      {/* Filled wedge */}
      <path d={arcPath} fill="#101010" />
    </svg>
  );
}
